import { takeEvery, all, call, put } from 'redux-saga/effects';
import { push } from 'connected-react-router';
import { SweetAlertResult } from 'sweetalert2';
import apiService from 'app/bff/services/apiService';
import { AuthStatusIdEnum } from 'app/bff/enums/auth';
import { AaSigninResp } from 'app/bff/models/aa/aaSignin';
import { AaOtpSendcodeResp } from 'app/bff/models/aa/aaOtpSendcode';
import { AaOtpVerifyResp } from 'app/bff/models/aa/aaOtpVerify';
import { MbUpdateinfoResp } from 'app/bff/models/mb/mbUpdateinfo';
import { ROUTES } from 'app/core/router/paths';
import modalService from 'app/core/services/modalService';
import { ProcessRouteMatchesStep } from 'app/features/Member/Settings/Email/types';
import { sagaBoundary } from 'app/store/service';
import { fetchMemberProfileAction } from 'app/store/common/actions';
import { MEMBER_SETTING_EMAIL__AUTH_PASS_PHRASE, MEMBER_SETTING_EMAIL__SEND_EMAIL_OTP, MEMBER_SETTING_EMAIL__AUTH_EMAIL_OTP, MEMBER_SETTING_EMAIL__SETUP_EMAIL, AuthPassPhraseAction, SendEmailOTPAction, AuthEmailOTPAction, SetupEmailAction } from './types';
import { setAccessibleStepAction, sendEmailOTPDoneAction, setupEmailAction } from './actions';

/**
 * @description 驗證會員密碼
 */
function * authPassPhrase (action: AuthPassPhraseAction) {
  const response: AaSigninResp = yield call(apiService.postAaSignin, action.payload.args);
  if (response && response.userResult === AuthStatusIdEnum.Success) {
    // 將「輸入新電子信箱」(Step-2-1) 設為可訪問步驟
    yield put(setAccessibleStepAction(ProcessRouteMatchesStep[ROUTES.MEMBER__SETTINGS__EMAIL__SETUP]));
    // 導頁至「輸入新電子信箱」(Step-2-1)
    yield put(push(ROUTES.MEMBER__SETTINGS__EMAIL__SETUP));
  } else if (response && !response.userResult) {
    // 登入失敗提醒跳窗
    modalService.base('系統提醒', '帳號已被鎖定, 請洽客服。');
    // 關閉「會員登入」Modal
    modalService.login(false);
  } else {
    modalService.base('系統提醒', '您輸入的密碼有誤，請重新輸入');
  }
}

/**
 * @description 發送信箱 OTP 驗證碼
 */
function * sendEmailOTP (action: SendEmailOTPAction) {
  const response: AaOtpSendcodeResp = yield call(apiService.postAaOtpSendcode, action.payload.args);
  if (response) {
    // 緩存 OTP 資料
    yield put(sendEmailOTPDoneAction(response));
    // 若有轉導路由則執行導頁
    if (action.payload.redirectRoute) {
      yield put(setAccessibleStepAction(ProcessRouteMatchesStep[action.payload.redirectRoute]));
      yield put(push(action.payload.redirectRoute));
    }
  }
}

/**
 * @description 驗證信箱 OTP 驗證碼
 */
function * authEmailOTP (action: AuthEmailOTPAction) {
  const response: AaOtpVerifyResp = yield call(apiService.postAaOtpVerify, action.payload.authEmailOTPArgs);
  if (response) {
    // 設置會員電子郵件
    yield put(setupEmailAction(action.payload.setupEmailArgs));
  }
}

/**
 * @description 設置會員電子郵件
 */
function * setupEmail (action: SetupEmailAction) {
  const response: MbUpdateinfoResp = yield call(apiService.postMbUpdateinfo, action.payload.args);
  if (response) {
    // 重新取得會員資料
    yield put(fetchMemberProfileAction({}));
    // 變更完成跳窗
    const result: SweetAlertResult = yield modalService.base('變更完成', '您的電子信箱已變更完成', '回會員中心');
    if (result.value && result.isConfirmed) {
      // 導頁至「會員中心」
      yield put(push(ROUTES.MEMBER__PROFILE));
    } else {
      // 導頁至主首頁
      yield put(push(ROUTES.HOME__MAIN));
    }
  }
}

export default function * watchEmailSaga () {
  yield all([
    takeEvery(MEMBER_SETTING_EMAIL__AUTH_PASS_PHRASE, sagaBoundary(authPassPhrase)),
    takeEvery(MEMBER_SETTING_EMAIL__SEND_EMAIL_OTP, sagaBoundary(sendEmailOTP)),
    takeEvery(MEMBER_SETTING_EMAIL__AUTH_EMAIL_OTP, sagaBoundary(authEmailOTP)),
    takeEvery(MEMBER_SETTING_EMAIL__SETUP_EMAIL, sagaBoundary(setupEmail))
  ]);
}
